import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchBudgets, fetchBudgetStatus, saveBudgets } from '@/lib/api'
import { mutationErrorHandler } from '@/lib/utils'
import type { Budget, BudgetStatusItem } from '@/lib/api'
import { useScope } from '@/lib/scope-context'
import { useAuth } from '@/lib/auth-context'

export type { Budget, BudgetStatusItem as BudgetItem } from '@/lib/api'
import type { Category } from './use-categories'

export function hasSubcategoryBudgets(
  parentId: string,
  budgets: Budget[],
  categories: Category[],
): boolean {
  const childIds = new Set(categories.filter(c => c.parent_id === parentId).map(c => c.id))
  return budgets.some(b => childIds.has(b.category_id))
}

export function hasParentBudget(
  parentId: string,
  budgets: Budget[],
): boolean {
  return budgets.some(b => b.category_id === parentId)
}

export function useBudgetStatus(month: string, expenseType?: string | null) {
  const { scope } = useScope()
  const { user } = useAuth()
  const userId = user?.id
  const expType = expenseType ?? null

  const householdQuery = useQuery<BudgetStatusItem[]>({
    queryKey: ['budgets', 'status', month, 'household', null, expType],
    queryFn: async () => {
      const result = await fetchBudgetStatus(month, 'household', null, expType)
      return result.items || []
    },
  })

  const personalQuery = useQuery<BudgetStatusItem[]>({
    queryKey: ['budgets', 'status', month, 'personal', userId ?? null, expType],
    queryFn: async () => {
      const result = await fetchBudgetStatus(month, 'personal', userId, expType)
      return result.items || []
    },
    enabled: !!userId,
  })

  const data = useMemo(() => {
    const household = householdQuery.data ?? []
    const personal = personalQuery.data ?? []

    if (scope === 'household') return household
    if (scope === 'personal') return personal
    return [...household, ...personal]
  }, [scope, householdQuery.data, personalQuery.data])

  const isLoading = householdQuery.isLoading || personalQuery.isLoading

  return { data, isLoading }
}

export function useBudgets(userId?: string | null) {
  return useQuery<Budget[]>({
    queryKey: ['budgets', userId ?? null],
    queryFn: () => fetchBudgets(userId),
  })
}

export function useSaveBudgetPair() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      household,
      personal,
    }: {
      household: { budgets: Array<{ category_id: string; amount: number | null }> }
      personal: { budgets: Array<{ category_id: string; amount: number | null }>; userId: string }
    }) => {
      await Promise.all([
        saveBudgets(household.budgets),
        saveBudgets(personal.budgets, personal.userId),
      ])
    },
    onSuccess: () => {
      toast.success('予算を保存しました')
      queryClient.invalidateQueries({ queryKey: ['budgets'] })
    },
    onError: mutationErrorHandler('保存に失敗しました'),
  })
}
